import SelectField from "../../../../common/SelectField";

/**
 * Partnership plan variables — PCP copay and referral bullet options.
 *
 * @param {object} props
 * @param {object} props.form
 * @param {boolean} props.isPartnershipPlan
 * @param {boolean} props.isPcpCopaySelected
 * @param {Record<string, string>} props.validationErrors
 * @param {(title: string) => void} props.onPcpCopaySelect
 * @param {(value: string) => void} props.onPcpCopayOtherChange
 * @param {(referralTitle: string, optionLabel: string) => void} props.onReferralSelect
 * @param {(referralTitle: string, value: string) => void} props.onReferralCustomChange
 * @returns {import('react').JSX.Element}
 */
const PartnershipVariables = ({
    form,
    isPartnershipPlan,
    isPcpCopaySelected,
    validationErrors,
    onPcpCopaySelect,
    onPcpCopayOtherChange,
    onReferralSelect,
    onReferralCustomChange,
}) => {
    const selectedPcpCopay = form.pcpCopay.find((option) => option.isSelected);

    return (
        <section className="plan-notification-panel plan-notification-panel--partnership">
            <h2 className="plan-notification-panel__title">
                Partnership Plan variables
            </h2>
            <p className="plan-notification-panel__required">
                <span aria-hidden>*</span> All fields below are required. The following
                variables are used in Partnership Plan communications. This is driven
                off the communications library selected above. If the plan is not a
                Partnership Plan, select a different communications library.
            </p>

            <SelectField
                id="pcp-copay"
                label="PCP copay"
                variant="notification"
                value={selectedPcpCopay?.title ?? ""}
                onChange={onPcpCopaySelect}
                placeholderOption="Select PCP copay"
                options={form.pcpCopay.map((option) => ({
                    value: option.title,
                    label: option.title,
                }))}
                helperText='If the PCP visits are a $0 copay, this value should be "FREE" unless the client requests to put "$0" instead.'
                disabled={!isPartnershipPlan}
                error={validationErrors.pcpCopay}
            />

            {selectedPcpCopay?.title === "Other" && isPartnershipPlan ? (
                <div className="form-input">
                    <label htmlFor="pcp-copay-other">PCP copay value</label>
                    <input
                        id="pcp-copay-other"
                        type="text"
                        value={selectedPcpCopay.value ?? ""}
                        onChange={(event) =>
                            onPcpCopayOtherChange(event.target.value)
                        }
                    />
                </div>
            ) : null}

            <p className="plan-notification-field__helper">
                The following variables are used to describe services that do NOT
                require a referral. This will be included in a reminder for members to
                get referrals. Please use the defaults provided below, unless the
                client has asked for customization to the language.
            </p>

            {form.referralOptions.map((referralGroup) => (
                <fieldset
                    key={referralGroup.title}
                    className="plan-notification-referral-group"
                    disabled={isPcpCopaySelected}
                >
                    <legend>{referralGroup.title}</legend>
                    {referralGroup.options.map((option) => {
                        const optionId = `${referralGroup.title}-${option.label.title}`;

                        return (
                            <div key={optionId} className="plan-notification-referral-option">
                                <label htmlFor={optionId}>
                                    <input
                                        id={optionId}
                                        type="radio"
                                        name={referralGroup.title}
                                        checked={Boolean(option.isSelected)}
                                        onChange={() =>
                                            onReferralSelect(
                                                referralGroup.title,
                                                option.label.title,
                                            )
                                        }
                                    />
                                    <span>{option.label.title}</span>
                                </label>
                                {option.isCustomInput && option.isSelected ? (
                                    <input
                                        type="text"
                                        aria-label={`${referralGroup.title} custom value`}
                                        placeholder="Other"
                                        value={option.value ?? ""}
                                        onChange={(event) =>
                                            onReferralCustomChange(
                                                referralGroup.title,
                                                event.target.value,
                                            )
                                        }
                                    />
                                ) : null}
                            </div>
                        );
                    })}
                </fieldset>
            ))}

            {validationErrors.referralOptions ? (
                <p className="plan-notification-field__error" role="alert">
                    {validationErrors.referralOptions}
                </p>
            ) : null}
        </section>
    );
};

export default PartnershipVariables;
